import {
  validateDocumentPatch,
  validateModelOutput,
} from './document_patch_schema.js';

export { validateDocumentPatch, validateModelOutput };

import * as diff from 'diff';

import {
  ATOM_TOKEN_PATTERN,
  markRange,
  rangeFor,
  splitOnAtoms,
  unescapeFromProjection,
} from './document_text_index.js';

const NAMED_TYPOGRAPHY_ENTITIES = [
  [/&nbsp;/gi, ' '],
  [/&(?:rsquo|lsquo|sbquo|apos|prime);/gi, "'"],
  [/&(?:ldquo|rdquo|bdquo|quot);/gi, '"'],
  [/&(?:ndash|mdash|minus);/gi, '-'],
];
const TYPOGRAPHIC_SPACES = /[\u00a0\u2007\u202f\u2009\u200a\u2003\u2002]/g;
const TYPOGRAPHIC_APOSTROPHES = /[\u2018\u2019\u201b\u2032]/g;
const TYPOGRAPHIC_QUOTES = /[\u201c\u201d\u2033]/g;
const TYPOGRAPHIC_DASHES = /[\u2010-\u2015\u2212]/g;
const MARKDOWN_EMPHASIS = /[*`]/g;
// The projection owns the token grammar; this only anchors it to a whole token.
const ATOM_TOKEN = new RegExp(`^${ATOM_TOKEN_PATTERN}$`);
// An atom whose label is only placeholder glyphs — underscores, dashes, dots,
// spaces — i.e. an unset field with nothing to assert. Deliberately still
// `[fxnt]`: a tracked change whose text is a row of underscores is a real
// deletion of a signature line, not a field waiting to be filled in, so its
// label must survive the fold rather than be blanked.
const ATOM_PLACEHOLDER_LABEL = /⟦([fxnt])(\d+):[\s_.\-\u2013\u2014]*⟧/g;
const REVIEW_ITEM_CLASS = 'diff-item';
const REVIEW_PRIMARY_CLASS = 'diff-item--primary';
const REVIEW_PARTIAL_CLASS = 'diff-item--partial';
const REVIEW_PARTIAL_POSITION_CLASSES = {
  first: 'diff-item--partial--first',
  middle: 'diff-item--partial--middle',
  last: 'diff-item--partial--last',
};
const REVIEW_TYPE_CLASSES = {
  ins: 'diff-item--insertion',
  del: 'diff-item--deletion',
};

/**
 * The closed set of unsupported-change categories a refusal can name. Every
 * proposal shape this module accepts is a content change; formatting-only
 * changes (bold, indentation, spacing) are deliberately out of scope for v1 and
 * have no reason here, because there is no channel that could carry one. The
 * projection flattens inline formatting, and the tenant-import path cannot see
 * it either: `CloneBlockLevelContentForHashing`
 * (../LeasePilot.HTML2DocX/OpenXmlPowerTools/WmlComparer.cs:3466, returning null
 * for `w:pPr` and `w:rPr` at :3473-3474) strips both before hashing, so a
 * formatting-only edit hashes identical to the original and never surfaces as a
 * diff at all.
 */
export const UNSUPPORTED_REASONS = Object.freeze({
  requiresOverride: 'requires_override',
  templateStatic: 'template_static',
  atomReorder: 'atom_reorder',
  crossReferenceBlock: 'cross_reference_block',
  crossReferenceTarget: 'cross_reference_target',
});

// No longer shown to anyone. What the drafter reads is composed from the typed
// `reason` by NOTE_ACTIONS (document_note_placement.js), because a note now
// lands in the lease body rather than in a panel list and has to say what to do
// about it, not which check refused.
//
// These stay as the human-readable half of the payload — the string a support
// engineer reads next to the reason code, in a log or a captured response —
// and they stay in plain language for the same reason. Copy edits belong in
// NOTE_ACTIONS; editing here changes nothing a user sees.
const REJECTION_MESSAGES = {
  quote_not_found:
    "We couldn't find this wording in the lease, so there was nothing to change.",
  ambiguous:
    'This wording appears in more than one place in the lease, so we could not tell which one to change.',
  not_editable:
    'This wording is fixed template text rather than an editable field, so it cannot be changed here.',
  requires_override:
    'This wording comes from the form template. To change it, create an Override for the form.',
  atom_reorder:
    'This would move a value LeasePilot fills in automatically to a different place in the sentence.',
  mutates_atom:
    'This would edit a value LeasePilot fills in automatically. Change it in the deal terms instead.',
  cross_reference_block:
    'This paragraph contains a cross-reference that other parts of the lease rely on, so it cannot be deleted automatically.',
  cross_reference_target:
    'This needs a reference to a section that does not exist yet, which has to be added by hand.',
  overlapping_patch:
    'This overlaps another suggestion in the same place, so only the first one was applied.',
  field_conflict:
    'This also changes a value LeasePilot fills in automatically, so it is listed with the deal terms rather than as a tracked change.',
  source_quote_missing:
    'This suggestion did not cite anything from your source document, so it was not created.',
  table_row_not_found:
    'We could not match this to a single editable row in the table.',
  table_row_shape:
    'The suggested row does not match the columns in this table.',
};

// The reasons this module can put on a note. Exported so the placement layer's
// coverage can be asserted against it rather than against a hand-kept list that
// silently falls back to the generic sentence when the two drift.
export const REJECTION_REASONS = Object.freeze(Object.keys(REJECTION_MESSAGES));

function reject(reason, patch, noteDetails = {}) {
  return {
    ok: false,
    reason,
    // Rejections are data, not an empty result. The caller can pass this note
    // through the drafting-note channel without reconstructing lost evidence.
    note: {
      kind: 'drafter_note',
      reason,
      message: noteDetails.message || REJECTION_MESSAGES[reason],
      sourceQuote: patch.sourceQuote,
      rationale: patch.rationale,
      // The lease-side quotes travel with the refusal. A note is placed in the
      // document body (document_note_placement.js), and without these it has
      // nothing to place against — the same evidence gap that made a run of
      // "we couldn't find this wording" refusals impossible to diagnose.
      ...(patch.find || patch.after
        ? { anchor: patch.find || patch.after }
        : {}),
      ...(patch.contextBefore === undefined
        ? {}
        : { contextBefore: patch.contextBefore }),
      // The language the proposal wanted to add. A refused proposal still knows
      // what it wanted to say, and a note the drafter can place beats a note
      // that only explains.
      //
      // insert_block's `content` only — deliberately not a text_patch's
      // `replace`. That is an edit to wording already in the lease, and INSERT
      // places a new block, so offering it here would duplicate the passage
      // instead of changing it. Reading `replace` at all would also trip the
      // read-count canary in disjointness.test.js, which exists because the
      // compiler treats the replacement as read-exactly-twice.
      ...(patch.content ? { content: patch.content } : {}),
      // What this proposal would say as a paragraph of its own, if the edit it
      // wanted turns out to be impossible. Unlike `replace` this is safe to
      // insert next to the passage: the model wrote it to stand beside language
      // that still says something different, not to supersede it in place.
      ...(patch.fallbackContent
        ? { fallbackContent: patch.fallbackContent }
        : {}),
      ...noteDetails,
    },
  };
}

function replaceMapped(units, pattern, replacement) {
  const text = units.map(unit => unit.text).join('');
  const replaced = [];
  let cursor = 0;

  pattern.lastIndex = 0;
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    units.slice(cursor, match.index).forEach(unit => replaced.push(unit));

    // A function replacement receives the match groups, so a rule can depend on
    // what it matched (reducing an atom token to its ordinal needs this).
    const replacementText =
      typeof replacement === 'function' ? replacement(...match) : replacement;

    if (replacementText) {
      const matched = units.slice(match.index, match.index + match[0].length);
      replacementText.split('').forEach(text => {
        replaced.push({
          text,
          start: matched[0].start,
          end: matched[matched.length - 1].end,
        });
      });
    }
    cursor = match.index + match[0].length;
  }

  units.slice(cursor).forEach(unit => replaced.push(unit));
  return replaced;
}

function normalizeCompatibility(units) {
  const normalized = [];
  const text = units.map(unit => unit.text).join('');
  const segments = new Intl.Segmenter(undefined, {
    granularity: 'grapheme',
  }).segment(text);

  // NFKC can compose a base character with following combining marks. Keep
  // each extended grapheme together (including Hangul Jamo sequences) so
  // folding agrees with Ruby's whole-string NFKC while retaining a useful
  // projection range for the resulting character.
  for (const segment of segments) {
    const grapheme = units.slice(
      segment.index,
      segment.index + segment.segment.length,
    );
    segment.segment
      .normalize('NFKC')
      .split('')
      .forEach(character => {
        normalized.push({
          text: character,
          start: grapheme[0].start,
          end: grapheme[grapheme.length - 1].end,
        });
      });
  }

  return normalized;
}

function sourceUnits(source) {
  return source.split('').map((text, index) => ({
    text,
    start: index,
    end: index + 1,
  }));
}

function foldMappedUnits(units) {
  return units.map(unit => {
    const text = unit.text
      .replace(TYPOGRAPHIC_SPACES, ' ')
      .replace(TYPOGRAPHIC_APOSTROPHES, "'")
      .replace(TYPOGRAPHIC_QUOTES, '"')
      .replace(TYPOGRAPHIC_DASHES, '-');

    return {
      text,
      start: unit.start,
      end: unit.end,
    };
  });
}

function flattenMappedUnits(units) {
  const offsets = [];
  let normalizedOffset = 0;

  units.forEach(unit => {
    for (let index = 0; index < unit.text.length; index += 1) {
      offsets[normalizedOffset] = { start: unit.start, end: unit.end };
      normalizedOffset += 1;
    }
  });

  return {
    text: units.map(unit => unit.text).join(''),
    offsets,
  };
}

// Exported for document_note_placement.js: a note has to be placed against the
// same folded text the locator searched, or a quote that failed to locate for a
// real reason would also fail to place for a spurious one.
export function foldTypographyWithMap(value, projection = false, stripSpace = false) {
  const source = String(value ?? '');
  let units = sourceUnits(source);

  // Projection escapes are synthetic and have no DOM source. Remove them
  // before applying the server's fold_typography rules so `\#` matches `#`.
  if (projection && unescapeFromProjection(source) !== source) {
    units = replaceMapped(units, /\\(?=[_*|#`\[\]])/g, '');
  }

  NAMED_TYPOGRAPHY_ENTITIES.forEach(([pattern, replacement]) => {
    units = replaceMapped(units, pattern, replacement);
  });
  units = replaceMapped(units, MARKDOWN_EMPHASIS, '');

  // Canonicalise PLACEHOLDER atom labels only. An unset lease-var renders as a
  // run of underscores, so its label carries no information and a model that
  // miscounts the run fails to locate text it quoted correctly — the dominant
  // cause of "lease anchor was not found" refusals.
  //
  // Deliberately NOT reducing every atom to its ordinal. A populated label is a
  // meaningful assertion: ⟦f0:Landlord⟧ against a document showing ⟦f0:Tenant⟧
  // is the model proposing a different field VALUE, which must fail this match
  // and route to field_conflict. Collapsing all labels made that tampering
  // invisible and broke the prompt-injection and atom-mutation specs.
  units = replaceMapped(
    units,
    ATOM_PLACEHOLDER_LABEL,
    (match, kind, ordinal) => `⟦${kind}${ordinal}:⟧`,
  );

  units = normalizeCompatibility(units);
  units = foldMappedUnits(units);

  // Collapse whitespace runs, exactly as the server's normalize_for_quote_match
  // does with /[[:space:]]+/. Without it the two halves of one check disagree:
  // the server grounds a quote and the client refuses the same quote as
  // quote_not_found, so a proposal that passed every server-side gate arrives
  // and is dropped.
  //
  // It happens because the model tidies whitespace it did not author. A
  // projection paragraph break is "\n \n" — newline, space, newline — and the
  // model writes "\n\n"; a double space inside a sentence comes back single.
  // Neither is a claim about different text.
  //
  // Safe through replaceMapped, which is what makes this fold usable for
  // locating at all: the offset table keeps pointing at real source positions
  // even as the folded text shrinks.
  units = replaceMapped(units, /[\s\u00a0]{2,}/g, ' ');

  // A last resort for `locate`, never the default: whitespace removed outright
  // rather than collapsed. Collapsing cannot help when the model ADDS a space
  // the lease does not have, which is what it does around template blanks.
  //
  // The run of underscores goes the same way, and for the same reason one rung
  // down: a model that miscounts a blank has not quoted different text. The
  // lease writes "[___ percent (__%)]" and the model wrote "(___%)" — three
  // underscores where the lease has two — which cost an agreed relocation
  // standard a place in the document and left the drafter a note beside the
  // very paragraph it was meant to change. The length of a blank carries no
  // meaning; it is the absence of a value.
  if (stripSpace) {
    units = replaceMapped(units, /[\s\u00a0]+/g, '');
    units = replaceMapped(units, /_{2,}/g, '_');
  }

  return flattenMappedUnits(units);
}

export function foldTypography(value) {
  return foldTypographyWithMap(value).text;
}

function allOccurrences(haystack, needle) {
  const occurrences = [];
  let cursor = 0;

  while (cursor <= haystack.length - needle.length) {
    const occurrence = haystack.indexOf(needle, cursor);
    if (occurrence === -1) break;
    occurrences.push(occurrence);
    cursor = occurrence + 1;
  }

  return occurrences;
}

/**
 * Narrow duplicate matches using the model's `contextBefore`.
 *
 * This only ever narrows. `contextBefore` answers "which of these identical
 * passages did you mean" — it is not a second assertion about the document that
 * a unique match has to survive. Enforced as a precondition it refused patches
 * whose location was never in doubt, because the model writes context the way a
 * person points at a passage: the heading above it, or the opening of the
 * paragraph it sits in, never the exact characters that immediately precede it.
 * In one live run that took every text_patch in the response, while every
 * insert_block — same model, same quotes, no such gate — applied cleanly.
 *
 * Two rungs, loosest last, for the two shapes the model actually produces:
 *   1. ends with the context, ignoring the whitespace that separates a heading
 *      or a sentence from whatever follows it. That whitespace belongs to the
 *      document's structure; the model never authored it.
 *   2. contains the context anywhere earlier — what a truncated paragraph
 *      prefix satisfies.
 *
 * When neither selects anything the candidates are returned untouched, so the
 * refusal is `ambiguous` — "this matches more than one place in the lease" —
 * rather than a `quote_not_found` that tells the drafter the lease has no such
 * language while the lease has two of it.
 */
function narrowByContextBefore(occurrences, haystack, contextBefore) {
  if (contextBefore === undefined) return occurrences;

  const context = foldTypographyWithMap(contextBefore, true).text.trim();
  if (!context) return occurrences;

  const adjacent = occurrences.filter(occurrence =>
    haystack.slice(0, occurrence).trimEnd().endsWith(context),
  );
  if (adjacent.length > 0) return adjacent;

  // Not adjacent, so take the occurrence the context actually introduces: the
  // first one after it, with no other candidate in between.
  //
  // A plain "context appears somewhere earlier" test keeps every later
  // occurrence too, which is no narrowing at all where it is needed most. An
  // insurance article lists its policies in order — commercial general
  // liability at (i), environmental legal liability at (viii) — and each states
  // the same limits, so a quote of "of not less than One Million ... per
  // occurrence, Two Million ... aggregate" matches both. Both sit after
  // "(i) Commercial general liability insurance", so both survived, and an
  // agreed limit reached the drafter as a note reading "this term matches more
  // than one place in the lease" while carrying the very context that said
  // which place.
  const introduced = occurrences.filter(occurrence => {
    const before = haystack.slice(0, occurrence);
    const contextAt = before.lastIndexOf(context);
    if (contextAt === -1) return false;

    return !occurrences.some(other => other > contextAt && other < occurrence);
  });
  if (introduced.length > 0) return introduced;

  const earlier = occurrences.filter(occurrence =>
    haystack.slice(0, occurrence).includes(context),
  );
  return earlier.length > 0 ? earlier : occurrences;
}

function atomParts(token) {
  const match = ATOM_TOKEN.exec(token);
  if (!match) throw new TypeError(`Invalid atom token: ${token}`);

  return {
    kind: match[1],
    ordinal: Number(match[2]),
    label: match[3],
  };
}

/**
 * Assert that a replacement preserves the document-ordered atom ordinals.
 * Labels are intentionally checked by the caller: a changed label is a field
 * proposal to route through field_conflict, not a prose patch to reject.
 */
export function assertSameAtomSequence(F, R) {
  const findOrdinals = F.atoms.map(atom => atomParts(atom).ordinal);
  const replaceOrdinals = R.atoms.map(atom => atomParts(atom).ordinal);
  const sameSequence =
    findOrdinals.length === replaceOrdinals.length &&
    findOrdinals.every((ordinal, index) => ordinal === replaceOrdinals[index]);

  if (!sameSequence) {
    throw new RangeError('Patch changes the ordered atom sequence');
  }
}

// Detected between find and replace rather than against the document, because
// only that pair carries the model's intent. A find is matched after folding,
// so one that merely transcribes a label with different typography — or drops
// the Markdown emphasis the fold removes from both sides — still matches and
// proposes no field change. Comparing the document's label against the
// replacement would read those as conflicts they are not. `position` is carried
// out so the caller can report the document's own label as `observed`.
function atomMetadataChanges(F, R) {
  const changes = [];

  F.atoms.forEach((token, position) => {
    const before = atomParts(token);
    const after = atomParts(R.atoms[position]);
    if (before.kind !== after.kind || before.label !== after.label) {
      changes.push({ position, before, after });
    }
  });

  return changes;
}

/** True when two ordinal lists hold the same atoms, in any order. */
function sameOrdinalMultiset(left, right) {
  if (left.length !== right.length) return false;

  const sortedLeft = [...left].sort((a, b) => a - b);
  const sortedRight = [...right].sort((a, b) => a - b);
  return sortedLeft.every((ordinal, index) => ordinal === sortedRight[index]);
}

function overlaps(start, end, span) {
  return span.start < end && span.end > start;
}

function elementForNode(node) {
  if (!node) return null;
  return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
}

// These predicates intentionally mirror the tenant-import policy helpers in
// lease_editor_controller.js. Generated proposals run the same policy before
// writing review markup; the import path applies it after creating a diff item
// and therefore has to render that item as blocked.
function isChangeDocumentStructure(node) {
  const leaseVar = elementForNode(node)?.closest('lease-var');
  return leaseVar?.getAttribute('non-click') === 'true';
}

function isWithinLeaseVar(node) {
  return !!elementForNode(node)?.closest('lease-var');
}

function isWithinCrossReference(node) {
  return !!elementForNode(node)?.closest('crr');
}

function isWithinContentEditable(node) {
  const element = elementForNode(node);
  const editable = element?.matches('.editable')
    ? element
    : element?.closest('.editable');

  return (
    !!editable &&
    editable.getAttribute('contenteditable-type') !== 'building-variable'
  );
}

function isWithinAutoNumber(node) {
  const numbered = elementForNode(node)?.closest('[list]');
  return !!numbered?.closest('.editable');
}

const PROTECTED_REGION_POLICIES = [
  {
    test: isChangeDocumentStructure,
    region: 'document_structure',
    message:
      'The proposed change would modify automation-owned document structure.',
  },
  {
    test: isWithinLeaseVar,
    region: 'lease_var',
    message: 'The proposed change targets an automated lease variable.',
  },
  {
    test: isWithinCrossReference,
    region: 'cross_reference',
    message: 'The proposed change targets a generated cross-reference.',
  },
  {
    test: isWithinAutoNumber,
    region: 'auto_number',
    message: 'The proposed change targets automatic numbering.',
  },
];

function nodesTouchedByRange(range) {
  const touched = new Set([range.startContainer, range.endContainer]);
  if (range.collapsed) return [...touched];

  const commonAncestor = range.commonAncestorContainer;
  if (commonAncestor.nodeType === Node.TEXT_NODE) return [...touched];

  const walker = range.startContainer.ownerDocument.createTreeWalker(
    commonAncestor,
    NodeFilter.SHOW_TEXT,
  );
  let current;
  while ((current = walker.nextNode())) {
    if (range.intersectsNode(current)) touched.add(current);
  }

  return [...touched];
}

function protectedRegionForRange(range) {
  const nodes = nodesTouchedByRange(range);

  for (const policy of PROTECTED_REGION_POLICIES) {
    if (nodes.some(policy.test)) return policy;
  }

  if (nodes.some(node => !isWithinContentEditable(node))) {
    return {
      reason: 'not_editable',
      region: 'content_editable',
      message: 'The proposed change is outside editable lease text.',
    };
  }

  return null;
}

class AtomDisjointnessError extends RangeError {}

/**
 * Assert that no projection range emitted by the compiler touches an opaque
 * automation-owned atom. This runs in every build so development and tests
 * exercise the same fail-closed apply boundary as production.
 */
export function assertAtomDisjoint(emittedRanges, atomSpans = []) {
  const overlap = emittedRanges.find(range =>
    atomSpans.some(atom =>
      overlaps(range.projectedStart, range.projectedEnd, atom),
    ),
  );

  if (overlap) {
    throw new AtomDisjointnessError(
      `Emitted range [${overlap.projectedStart}, ${overlap.projectedEnd}) overlaps an atom span`,
    );
  }
}

/**
 * Resolve an agent-authored quote against the immutable text projection.
 * Success includes exact projection offsets and their DOM Range; every refusal
 * carries a typed drafter note, and no branch selects a merely-nearby quote.
 */
export function locate(index, patch) {
  let haystack = foldTypographyWithMap(index.text, true);
  let needle = foldTypographyWithMap(patch.find, true).text;
  if (!needle) return reject('quote_not_found', patch);

  let occurrences = allOccurrences(haystack.text, needle);

  // Retried without whitespace at all, and only when the ordinary search found
  // nothing. A lease is full of blanks — "by more than \[\_\_\_ percent
  // (\_\_%)\]" — and filling one in is a patch that has no choice but to quote
  // it. A model transcribing that run reliably mis-spaces it: one live anchor
  // put a space before the closing parenthesis and a two-hundred-character
  // quote missed by that single character.
  //
  // Safe because it is a fallback rather than a widening: an exact match always
  // wins, the whole needle must still appear, and two passages of a lease do
  // not differ from one another only in whitespace. The offset table is built
  // by the same mapped fold, so the range still points at real source
  // positions — spaces included, which is what the edit has to replace.
  if (occurrences.length === 0) {
    const looseHaystack = foldTypographyWithMap(index.text, true, true);
    const looseNeedle = foldTypographyWithMap(patch.find, true, true).text;
    const looseOccurrences = looseNeedle
      ? allOccurrences(looseHaystack.text, looseNeedle)
      : [];

    if (looseOccurrences.length > 0) {
      haystack = looseHaystack;
      needle = looseNeedle;
      occurrences = looseOccurrences;
    }
  }

  if (occurrences.length === 0) return reject('quote_not_found', patch);

  // Only consulted when there is a choice to make. One occurrence is one
  // location; no context can move the edit somewhere else, so nothing is left
  // for this to decide.
  if (occurrences.length > 1) {
    occurrences = narrowByContextBefore(
      occurrences,
      haystack.text,
      patch.contextBefore,
    );
  }

  if (occurrences.length > 1) return reject('ambiguous', patch);

  const normalizedStart = occurrences[0];
  const normalizedEnd = normalizedStart + needle.length;
  const start = haystack.offsets[normalizedStart].start;
  const end = haystack.offsets[normalizedEnd - 1].end;

  if (
    (index.spans || []).some(
      span => span.kind === 'frozen' && overlaps(start, end, span),
    )
  ) {
    const templateStatic = (index.spans || []).some(
      span =>
        span.kind === 'frozen' &&
        overlaps(start, end, span) &&
        !elementForNode(span.node)?.closest('[section-id]'),
    );
    if (templateStatic) {
      return reject(UNSUPPORTED_REASONS.requiresOverride, patch, {
        unsupportedReason: UNSUPPORTED_REASONS.templateStatic,
      });
    }
    return reject('not_editable', patch);
  }

  const documentAtoms = (index.atomSpans || []).filter(atom =>
    overlaps(start, end, atom),
  );
  const F = splitOnAtoms(patch.find);
  const R = splitOnAtoms(patch.replace);
  const findAtoms = F.atoms.map(atomParts);
  const findAtomOrdinals = findAtoms.map(atom => atom.ordinal);
  // Kind as well as ordinal. Automation atoms are numbered in document order
  // while a tracked change carries its diff-index, so ⟦f31:…⟧ and ⟦d31:…⟧ are
  // two different atoms wearing the same number; comparing ordinals alone lets
  // a quote that names one pass against the other standing in the document.
  if (
    documentAtoms.length !== findAtoms.length ||
    documentAtoms.some(
      (atom, position) =>
        atom.ordinal !== findAtoms[position].ordinal ||
        atom.kind !== findAtoms[position].kind,
    )
  ) {
    return reject('mutates_atom', patch);
  }

  try {
    assertSameAtomSequence(F, R);
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    // One assertion, three different proposals: a replacement that permutes the
    // atoms it quoted, one that drops some, and one that invents ordinals the
    // quote never carried. Only the first is a reorder, so only the first may
    // say so — naming a dropped atom `atom_reorder` would hand the drafter a
    // note describing an operation they did not propose, which is worse than
    // the generic reason it replaced.
    const replaceAtomOrdinals = R.atoms.map(atom => atomParts(atom).ordinal);
    if (sameOrdinalMultiset(findAtomOrdinals, replaceAtomOrdinals)) {
      return reject(UNSUPPORTED_REASONS.atomReorder, patch);
    }
    return reject('mutates_atom', patch, {
      message:
        replaceAtomOrdinals.length < findAtomOrdinals.length
          ? 'The proposed change would remove automated lease content the quote carried.'
          : 'The proposed change would introduce automated lease content the quote did not carry.',
    });
  }

  const metadataChanges = atomMetadataChanges(F, R);
  if (
    metadataChanges.some(change => change.before.kind !== change.after.kind)
  ) {
    return reject('mutates_atom', patch);
  }
  if (metadataChanges.length > 0) {
    return {
      // The atom half of this patch routes to the deal-terms path as
      // `conflicts`, but the prose half is refused outright. It carries the
      // same `drafter_note` every other refusal does so the call site can
      // surface it without special-casing this reason.
      ...reject('field_conflict', patch),
      // `observed` is what the lease says, read off the index, not the model's
      // transcription of it in `find`: the two differ whenever folding removed
      // something, and a drafter comparing observed against sourceValue is
      // owed the document's own rendering of the field.
      conflicts: metadataChanges.map(({ position, after }) => ({
        kind: 'field_conflict',
        atom: documentAtoms[position].ordinal,
        observed: documentAtoms[position].label,
        sourceValue: after.label,
        sourceQuote: patch.sourceQuote,
        rationale: patch.rationale,
      })),
    };
  }

  try {
    return { ok: true, start, end, range: rangeFor(index, start, end) };
  } catch (error) {
    // Synthetic Markdown and atom-only boundaries have no editable DOM
    // position. Refuse rather than snapping the quote to neighbouring prose.
    //
    // Only `resolve`'s "no DOM source position" refusal means that; it is the
    // one thing this module raises a RangeError for. Anything else is a bug in
    // the index or in this ladder, and reporting it to the drafter as a benign
    // "not in an editable section" would hide the very failure the ladder
    // exists to catch. Let it escape.
    if (!(error instanceof RangeError)) throw error;
    return reject('not_editable', patch);
  }
}

const WHITESPACE = /\s/;

// `diffWords` ignores whitespace differences, and pays for that by handing back
// parts whose `value` is spelled the way the *replacement* spells it: given
// "Tenant shall pay Base Rent monthly." against "Tenant  shall pay Base Rent
// annually.", the unchanged part is 28 characters of the replacement where the
// find has 27. Advancing a cursor by `part.value.length` therefore accumulates
// the whitespace drift and shifts every later hunk — the deletion above lands on
// "onthly." instead of "monthly", which on accept leaves "Rent mannually" in the
// lease. Measure each part against the find text itself instead, treating a
// whitespace run on either side as matching a whitespace run on the other, which
// is exactly the equivalence `diffWords` used to declare the part unchanged.
function findLengthOf(before, cursor, value) {
  let beforeIndex = cursor;
  let valueIndex = 0;

  while (valueIndex < value.length) {
    if (
      WHITESPACE.test(value[valueIndex]) ||
      (beforeIndex < before.length && WHITESPACE.test(before[beforeIndex]))
    ) {
      while (valueIndex < value.length && WHITESPACE.test(value[valueIndex])) {
        valueIndex += 1;
      }
      while (
        beforeIndex < before.length &&
        WHITESPACE.test(before[beforeIndex])
      ) {
        beforeIndex += 1;
      }
      continue;
    }

    // Only whitespace may differ. Anything else means the part is not the run
    // of `before` the cursor says it is, and every offset from here on would be
    // a guess — the one thing this module refuses to do.
    if (before[beforeIndex] !== value[valueIndex]) {
      throw new RangeError('Diff part does not match the find text');
    }
    beforeIndex += 1;
    valueIndex += 1;
  }

  return beforeIndex - cursor;
}

function changedHunks(differences, before) {
  const hunks = [];
  let beforeOffset = 0;
  let hunk = null;

  const finishHunk = () => {
    if (!hunk) return;
    hunks.push(hunk);
    hunk = null;
  };

  const openHunk = () => {
    if (hunk) return;
    hunk = {
      start: beforeOffset,
      end: beforeOffset,
      inserted: '',
    };
  };

  differences.forEach(part => {
    // An addition consumes nothing of `before`, so it neither advances the
    // cursor nor needs to be found in it.
    if (part.added) {
      openHunk();
      hunk.inserted += part.value;
      return;
    }

    const consumed = findLengthOf(before, beforeOffset, part.value);
    if (!part.removed) {
      finishHunk();
      beforeOffset += consumed;
      return;
    }

    openHunk();
    beforeOffset += consumed;
    hunk.end = beforeOffset;
  });
  finishHunk();

  return hunks;
}

function projectionUnits(value) {
  const units = [];

  for (let sourceOffset = 0; sourceOffset < value.length; sourceOffset += 1) {
    if (
      value[sourceOffset] === '\\' &&
      /[_*|#`\[\]]/.test(value[sourceOffset + 1] || '')
    ) {
      sourceOffset += 1;
    }
    units.push({
      text: value[sourceOffset],
      start: sourceOffset,
      end: sourceOffset + 1,
    });
  }

  return units;
}

function unitBoundary(units, sourceOffset) {
  const boundary = units.findIndex(unit => unit.start >= sourceOffset);
  return boundary === -1 ? units.length : boundary;
}

function targetBoundary(units, boundary, edge) {
  if (edge === 'start') {
    return boundary < units.length
      ? units[boundary].start
      : units[units.length - 1]?.end || 0;
  }
  return boundary > 0 ? units[boundary - 1].end : units[0]?.start || 0;
}

function proseOffsetMapper(documentProse, authoredProse) {
  const documentUnits = projectionUnits(documentProse);
  const authoredUnits = projectionUnits(authoredProse);
  const documentPlain = documentUnits.map(unit => unit.text).join('');
  const authoredPlain = authoredUnits.map(unit => unit.text).join('');

  if (documentPlain === authoredPlain) {
    return (sourceOffset, edge) =>
      targetBoundary(
        documentUnits,
        unitBoundary(authoredUnits, sourceOffset),
        edge,
      );
  }

  // `locate` deliberately permits typographic transcriptions. When the raw
  // prose differs, use the same folded maps to translate a diff boundary back
  // to the matching document character rather than treating the authored
  // string offset as a projection offset.
  //
  // Both of `locate`'s tolerances are tried here, in its order, and that
  // pairing is the contract: this must accept every quote `locate` accepts, or
  // a patch is found and then thrown out at the point of being applied. That is
  // what happened the first time the loose retry ever reached production — the
  // anchor was located, this threw, and the RangeError took down the whole
  // batch rather than the single patch.
  const mapper = (document, authored) => (sourceOffset, edge) =>
    targetBoundary(
      document.offsets,
      unitBoundary(authored.offsets, sourceOffset),
      edge,
    );

  const documentFolded = foldTypographyWithMap(documentProse, true);
  const authoredFolded = foldTypographyWithMap(authoredProse, true);
  if (documentFolded.text === authoredFolded.text) {
    return mapper(documentFolded, authoredFolded);
  }

  // `locate`'s last resort: whitespace removed outright and template blanks
  // collapsed. prepareTextPatch diffs against the document's own text in this
  // case, so in practice nothing reaches here — it stands so that a caller that
  // does not gets a mapping rather than an exception.
  const documentLoose = foldTypographyWithMap(documentProse, true, true);
  const authoredLoose = foldTypographyWithMap(authoredProse, true, true);
  if (documentLoose.text !== authoredLoose.text) {
    throw new RangeError('Located prose segment does not match patch prose');
  }

  return mapper(documentLoose, authoredLoose);
}

function markedInsertion(ownerDocument, text, attrs) {
  const source = ownerDocument.createTextNode(unescapeFromProjection(text));
  const container = ownerDocument.createDocumentFragment();
  container.appendChild(source);
  const range = ownerDocument.createRange();
  range.selectNodeContents(source);

  return markRange(range, 'ins', attrs)[0];
}

function closestReviewPid(range) {
  const start = range.startContainer;
  const element =
    start.nodeType === Node.ELEMENT_NODE ? start : start.parentElement;
  const paragraph = element?.closest('[pid]');

  return paragraph?.getAttribute('pid') || '';
}

// Generated markers deliberately use the tenant-import path's public DOM
// contract. The review controller discovers, groups, navigates, accepts and
// rejects requests entirely through these classes and attributes; provenance
// remains an optional extra attribute and does not create a second UI path.
function reviewMarkerAttrs(tag, range, attrs, diffIndex) {
  const diffPid = attrs['diff-pid'] ?? closestReviewPid(range);
  const requiredClasses = [
    REVIEW_ITEM_CLASS,
    REVIEW_TYPE_CLASSES[tag],
    REVIEW_PRIMARY_CLASS,
  ];
  const classes = new Set(
    `${attrs.class || ''} ${requiredClasses.join(' ')}`
      .trim()
      .split(/\s+/)
      .filter(Boolean),
  );

  return {
    ...attrs,
    class: [...classes].join(' '),
    'diff-item': `diffgram.items[${diffIndex}]`,
    'diff-index': String(diffIndex),
    'diff-pid': String(diffPid),
    'diff-type': 'text',
    'ng-click':
      tag === 'del'
        ? `annotationsHelper.enterIndividualRequestMode(${diffIndex})`
        : `annotationsHelper.jumpToRequest(${diffIndex})`,
  };
}

function insertAt(range, marker) {
  range.insertNode(marker);
  range.collapse(false);
}

// The words removed and the words put in their place are two requests, as they
// are in a tenant import: its diffgram is a flat list of items typed INSERTION
// or DELETION, one index each, and it has never had a paired replace at text
// level. Sharing one index made a card read Delete "shallmust" — the label
// taking its verb from the deletion and its text from both.
function applyHunk(index, hunk, attrs, deletionIndex, insertionIndex) {
  const { projectedStart: start, projectedEnd: end } = hunk;
  const deletionRange = rangeFor(index, start, end);
  const deletionAttrs = reviewMarkerAttrs(
    'del',
    deletionRange,
    attrs,
    deletionIndex,
  );
  const deletionMarkers =
    start === end ? [] : markRange(deletionRange, 'del', deletionAttrs);
  const insertionMarkers = [];

  if (hunk.inserted) {
    const ownerDocument = deletionRange.startContainer.ownerDocument;
    const insertionAttrs = reviewMarkerAttrs(
      'ins',
      deletionRange,
      attrs,
      insertionIndex,
    );
    const insertion = markedInsertion(
      ownerDocument,
      hunk.inserted,
      insertionAttrs,
    );

    if (deletionMarkers.length > 0) {
      deletionMarkers[deletionMarkers.length - 1].after(insertion);
    } else {
      insertAt(deletionRange, insertion);
    }
    insertionMarkers.push(insertion);
  }

  return { deletionMarkers, insertionMarkers };
}

/**
 * Compile one located text patch into DOM review markup. Opaque atom tokens are
 * structural separators only: each prose segment is diffed and marked within
 * the projection interval between its neighbouring atoms, so neither an atom
 * element nor its placeholder token can become a diff operand or DOM text.
 */
// Whether `locate` matched this quote only by its last resort — whitespace
// removed and template blanks collapsed — rather than exactly or by typography.
// When it did, the quote is a transcription of the passage rather than a copy
// of it, and the lease's own text is the one to work from.
function quoteNeedsDocumentText(documentProse, quoted) {
  if (documentProse === quoted) return false;

  const foldedDocument = foldTypographyWithMap(documentProse, true).text;
  const foldedQuote = foldTypographyWithMap(quoted, true).text;
  if (foldedDocument === foldedQuote) return false;

  return (
    foldTypographyWithMap(documentProse, true, true).text ===
    foldTypographyWithMap(quoted, true, true).text
  );
}

function prepareTextPatch(index, patch) {
  if (patch.kind !== 'text_patch') {
    throw new TypeError('compileTextPatch requires a text_patch');
  }

  const located = locate(index, patch);
  if (!located.ok) return located;

  const F = splitOnAtoms(patch.find);
  const R = splitOnAtoms(patch.replace);
  assertSameAtomSequence(F, R);
  const atoms = (index.atomSpans || []).filter(atom =>
    overlaps(located.start, located.end, atom),
  );
  const segmentBounds = F.prose.map((_prose, segmentIndex) => ({
    start: segmentIndex === 0 ? located.start : atoms[segmentIndex - 1].end,
    end:
      segmentIndex === atoms.length ? located.end : atoms[segmentIndex].start,
  }));

  const segments = F.prose
    .map((quoted, segmentIndex) => {
      const after = R.prose[segmentIndex];
      const bounds = segmentBounds[segmentIndex];
      const documentProse = index.text.slice(bounds.start, bounds.end);
      // Diff against the LEASE's text, not the model's transcription of it,
      // whenever the two matched only loosely. Diffing the transcription means
      // mapping every hunk boundary back through a fold that discarded
      // whitespace, and that mapping is ambiguous by construction: two source
      // positions collapse to one folded position, so a boundary lands a
      // character out and leaves a space before a semicolon nobody authored.
      // The document's own text has no such gap to bridge.
      const before = quoteNeedsDocumentText(documentProse, quoted)
        ? documentProse
        : quoted;
      if (before === after) return null;

      const differences = diff.diffWords(before, after);
      const mapOffset = proseOffsetMapper(documentProse, before);
      return {
        segmentIndex,
        before,
        after,
        differences,
        ...bounds,
        hunks: changedHunks(differences, before).map(hunk => ({
          ...hunk,
          projectedStart: bounds.start + mapOffset(hunk.start, 'start'),
          // A hunk that deletes nothing has one boundary, not two, so both ends
          // map through the same edge. Mapping them independently reads the
          // preceding unit's end for one and the following unit's start for the
          // other, which differ wherever a projection escape sits between them:
          // inserting a word before the "#" of "Suite \#300" then produced
          // start 7 > end 6 and aborted the whole compile on a RangeError.
          // 'start' is the surviving edge because it addresses the escaped
          // character itself; the backslash before it is synthetic and owns no
          // DOM position for `resolve` to return.
          projectedEnd:
            bounds.start +
            mapOffset(hunk.end, hunk.start === hunk.end ? 'start' : 'end'),
        })),
      };
    })
    .filter(Boolean);

  const emittedRanges = segments.flatMap(segment => segment.hunks);
  try {
    // Every emitted range is bounded by consecutive atom spans, so
    // disjointness holds by construction. Assert it at the apply boundary so
    // malformed input cannot turn a compiler invariant failure into a write.
    assertAtomDisjoint(emittedRanges, index.atomSpans || []);
  } catch (error) {
    if (!(error instanceof AtomDisjointnessError)) throw error;
    return reject('mutates_atom', patch);
  }

  // Resolve every prospective DOM write and apply the editor's protected-
  // region policy before the first marker is emitted. This is deliberately a
  // preflight over the complete patch: if any hunk is protected, no earlier
  // hunk may survive as an unusable red review item.
  const protectedRegion = emittedRanges
    .map(hunk =>
      protectedRegionForRange(
        rangeFor(index, hunk.projectedStart, hunk.projectedEnd),
      ),
    )
    .find(Boolean);
  if (protectedRegion) {
    return reject(protectedRegion.reason || 'mutates_atom', patch, {
      protectedRegion: protectedRegion.region,
      message: protectedRegion.message,
    });
  }

  return { ok: true, start: located.start, end: located.end, segments };
}

// `prepared.segments` and each segment's markers are stored in document order,
// so a flat walk yields the patch's markers in the order the review UI sees
// them via `querySelectorAll`.

// One HALF of one hunk is one review request. A word-level diff of a replaced
// paragraph produces a marker pair per hunk, and the review UI addresses a
// request by its `diff-index` — so numbering every hunk of a patch alike made
// the whole paragraph one request: clicking any fragment selected all of them,
// the card concatenated their text into Delete "OneTwo12TwoFour24", and the
// partial outline banded the lot into a single pink block. The tenant-import
// path has always numbered per deletion and per insertion; this now matches it.
//
// Primary and partial classes are therefore per request too. A deletion
// spanning several DOM nodes still gets the composed outline — that is what the
// partial family is for — but a deletion and the insertion replacing it no
// longer join into one.
function normalizeRequestMarkers(markers) {
  markers
    .slice(1)
    .forEach(marker => marker.classList.remove(REVIEW_PRIMARY_CLASS));

  const deletions = markers.filter(marker => marker.tagName === 'DEL');
  if (deletions.length < 2) return;

  deletions.forEach((marker, position) => {
    const placement =
      position === 0
        ? 'first'
        : position === deletions.length - 1
        ? 'last'
        : 'middle';

    marker.setAttribute('diff-partial-index', String(position));
    marker.classList.add(
      REVIEW_PARTIAL_CLASS,
      REVIEW_PARTIAL_POSITION_CLASSES[placement],
    );
  });
}

// One entry per REQUEST, in document order — a hunk's deletion, then the
// insertion that replaces it. That is the order their `diff-index` values run
// in, and the order a reader stepping through changes expects.
export function preparedRequests(prepared) {
  if (!prepared || !prepared.ok) return [];

  return prepared.segments
    .flatMap(segment => segment.markers || [])
    .flatMap(({ deletionMarkers, insertionMarkers }) =>
      [deletionMarkers, insertionMarkers].filter(markers => markers.length > 0),
    );
}

// How many slots a hunk needs: one for what it removes, one for what it adds.
function hunkRequestCount(hunk) {
  return (
    (hunk.projectedStart === hunk.projectedEnd ? 0 : 1) +
    (hunk.inserted ? 1 : 0)
  );
}

function applyPreparedTextPatch(index, prepared, attrs, firstDiffIndex) {
  // Numbered in document order even though they are applied in reverse: DOM
  // ranges point into the immutable index, so applying from the end keeps every
  // earlier range valid without offset compensation.
  let slot =
    firstDiffIndex +
    prepared.segments.reduce(
      (total, segment) =>
        total +
        segment.hunks.reduce((n, hunk) => n + hunkRequestCount(hunk), 0),
      0,
    );

  [...prepared.segments].reverse().forEach(segment => {
    segment.markers = segment.hunks
      .slice()
      .reverse()
      .map(hunk => {
        slot -= hunkRequestCount(hunk);
        // Deletion first: it is what the reader meets first in the document. A
        // pure insertion deletes nothing and takes the slot itself.
        const deletes = hunk.projectedStart !== hunk.projectedEnd;
        return applyHunk(index, hunk, attrs, slot, deletes ? slot + 1 : slot);
      })
      .reverse();
  });

  preparedRequests(prepared).forEach(normalizeRequestMarkers);
  return prepared;
}

// `diff-index` addresses a slot in the review controller's `diffgram.items`, so
// a caller with existing requests needs generated ones to continue past them.
// The attribute is read as the first index to use rather than as the index of
// every marker: a batch would otherwise collapse all of its patches onto one
// request id, and accepting any one of them would accept the lot.
function baseDiffIndex(attrs) {
  const base = attrs['diff-index'];
  if (base === undefined || base === null) return 0;

  const numeric = Number(base);
  if (!Number.isInteger(numeric) || numeric < 0) {
    throw new TypeError('attrs["diff-index"] must be a non-negative integer');
  }

  return numeric;
}

export function compileTextPatch(index, patch, attrs = {}) {
  const prepared = prepareTextPatch(index, patch);
  if (!prepared.ok) return prepared;

  return applyPreparedTextPatch(index, prepared, attrs, baseDiffIndex(attrs));
}

/**
 * Compile several text patches against one immutable projection. Every patch is
 * located and compiled before the first DOM write. Input order establishes
 * overlap priority; accepted ranges are then applied from the bottom of the
 * document so mutations cannot invalidate an earlier range. Each patch becomes
 * one review request, numbered from `attrs['diff-index']` by input position.
 */
export function compileTextPatches(index, patches, attrs = {}) {
  if (!Array.isArray(patches)) {
    throw new TypeError('compileTextPatches requires an array of text patches');
  }

  const base = baseDiffIndex(attrs);
  const prepared = [];
  const results = new Array(patches.length);

  patches.forEach((patch, patchIndex) => {
    const result = prepareTextPatch(index, patch);
    if (!result.ok) {
      results[patchIndex] = result;
      return;
    }

    const conflict = prepared.find(candidate =>
      overlaps(result.start, result.end, candidate.result),
    );
    if (conflict) {
      const rejection = reject('overlapping_patch', patch);
      rejection.note.conflictingPatchIndex = conflict.patchIndex;
      results[patchIndex] = rejection;
      return;
    }

    prepared.push({ patchIndex, result });
  });

  // A slot per request — one for what a hunk removes and one for what it adds —
  // in input order, so a patch owns a contiguous run of them.
  // Allocated before anything is applied because application runs backwards
  // through the document while numbering has to run forwards through the batch.
  // A refused patch emits no hunks and so consumes nothing, which keeps a batch
  // of single-hunk patches numbered exactly as it was: base, base + 1, base + 2.
  const firstSlots = new Array(patches.length).fill(base);
  let nextSlot = base;
  patches.forEach((_, patchIndex) => {
    firstSlots[patchIndex] = nextSlot;
    const entry = prepared.find(candidate => candidate.patchIndex === patchIndex);
    if (!entry) return;
    nextSlot += entry.result.segments.reduce(
      (total, segment) =>
        total +
        segment.hunks.reduce((n, hunk) => n + hunkRequestCount(hunk), 0),
      0,
    );
  });

  const applied = prepared
    .sort((left, right) => right.result.start - left.result.start)
    .map(({ patchIndex, result }) => {
      const appliedResult = applyPreparedTextPatch(
        index,
        result,
        attrs,
        firstSlots[patchIndex],
      );
      results[patchIndex] = appliedResult;
      return { patchIndex, result: appliedResult };
    });
  const rejected = results
    .map((result, patchIndex) => ({ patchIndex, result }))
    .filter(({ result }) => !result.ok);

  return {
    ok: rejected.length === 0,
    results,
    applied,
    rejected,
  };
}

function fieldChangeForConflict(index, conflict) {
  // Restricted to the field-bearing kinds while looking up, not after: a
  // tracked change reuses its diff-index as its ordinal, so an unfiltered
  // search can return ⟦d31:…⟧ for a conflict that named the lease-var ⟦f31:…⟧
  // and refuse a field change that was resolvable all along.
  const atom = (index.atomSpans || []).find(
    candidate =>
      candidate.ordinal === conflict.atom &&
      ['f', 't'].includes(candidate.kind),
  );

  if (!atom || !atom.fieldPath) {
    return reject('mutates_atom', conflict, {
      message: 'The referenced automated field could not be resolved.',
    });
  }

  return {
    ok: true,
    change: {
      path: atom.fieldPath.replace(/^lease\./, ''),
      field: atom.fieldPath.split('.').pop(),
      before: conflict.observed,
      after: conflict.sourceValue,
      extracted: conflict.sourceValue,
      current: conflict.observed,
      decision: null,
      applied: false,
      // Hoisted out of `proposal`, not merely carried on it. When this form has
      // no control for the field the change becomes an advisory one and then a
      // suggestion in the lease body, and that path reads the evidence off the
      // change itself. Left nested, the card fell back to stringifying the new
      // value — a drafter looking at a whole free-rent provision was shown
      // "Source quote: 12 months" and no rationale at all.
      sourceQuote: conflict.sourceQuote,
      rationale: conflict.rationale,
      fallbackContent: conflict.fallbackContent,
      proposal: conflict,
    },
  };
}

function closestPidElement(node) {
  const element = elementForNode(node);
  return element?.matches('[pid]') ? element : element?.closest('[pid]');
}

// Every kind the projection can issue, tracked changes included: a diff-index
// the model mistranscribed is the same failure as a mistranscribed atom
// ordinal, and leaving it unrepaired means the quote simply never grounds.
const ATOM_WITH_ORDINAL = new RegExp(ATOM_TOKEN_PATTERN, 'g');

/**
 * Correct an anchor quote's atom ordinals against the document.
 *
 * The model transcribes atom labels reliably and ordinals unreliably: in a live
 * run it anchored on `⟦n231:ARTICLE 32⟧` when n231 is ARTICLE 31 — it reused
 * the neighbouring article's number — and that one digit cost a whole proposal.
 *
 * This repairs the quote rather than loosening the search, so the offset map
 * built by `foldTypographyWithMap` still lines up. A label carried by more than
 * one ordinal is left exactly as it came: guessing between two real atoms would
 * be inventing a location, and leaving it means the quote simply does not
 * ground, which is the safe direction.
 */
export function repairAtomOrdinals(quote, documentText) {
  const source = String(quote ?? '');
  if (!source.includes('⟦')) return source;

  return source.replace(
    ATOM_WITH_ORDINAL,
    (token, kind, ordinal, label) => {
      const candidates = [
        ...String(documentText ?? '').matchAll(
          new RegExp(
            `⟦${kind}(\\d+):${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}⟧`,
            'g',
          ),
        ),
      ];
      if (candidates.length !== 1) return token;

      return `⟦${kind}${candidates[0][1]}:${label}⟧`;
    },
  );
}

/**
 * Resolve an insert_block's `after` quote through the exact text-patch locator.
 * The returned anchor is the existing DOM block itself: no model-authored or
 * synthetic identifier participates in locating the insertion point.
 */
export function locateInsertBlock(index, proposal) {
  const contentAtoms = splitOnAtoms(proposal.content).atoms;
  if (contentAtoms.some(token => atomParts(token).kind === 'x')) {
    return reject(UNSUPPORTED_REASONS.crossReferenceTarget, proposal);
  }
  if (contentAtoms.length > 0) {
    return reject('mutates_atom', proposal, {
      message: 'A new block cannot synthesize automation-owned content.',
    });
  }

  // Deliberately NOT `locate`. That is the text_patch locator, and it enforces
  // what editing text in place requires: the quote must fall inside editable
  // spans, must not straddle a frozen template region, must align with atoms,
  // and must resolve to a DOM range.
  //
  // Inserting a block after another block needs none of that. It needs to
  // identify one block. Borrowing the patch locator meant that any anchor
  // touching an article heading — which is static template text — was refused
  // as `requires_override` or `not_editable`, and in a real run that was every
  // single insert_block: the model quotes "⟦n130:ARTICLE 5⟧- SECURITY DEPOSIT"
  // and the paragraph under it, exactly as a person would point at a section.
  // Eight drafted clauses became drafting notes for a reason that had nothing
  // to do with where they were going.
  const haystack = foldTypographyWithMap(index.text, true);
  const needle = foldTypographyWithMap(
    repairAtomOrdinals(proposal.after, index.text),
    true,
  ).text;
  if (!needle) return reject('quote_not_found', proposal);

  const occurrences = allOccurrences(haystack.text, needle);
  if (occurrences.length === 0) return reject('quote_not_found', proposal);
  if (occurrences.length > 1) return reject('ambiguous', proposal);

  const start = haystack.offsets[occurrences[0]].start;
  const end = haystack.offsets[occurrences[0] + needle.length - 1].end;

  // The block the quote ENDS in: the new block goes after the last thing
  // quoted, not after the first.
  const tail = (index.spans || []).find(
    span => span.start < end && end <= span.end,
  );
  const anchor = tail ? closestPidElement(tail.node) : null;
  if (!anchor) return reject('not_editable', proposal);

  // A block-level insertion inside a table cell is structurally wrong, and the
  // list-item builder assumes body-level flow.
  if (anchor.closest('td, th')) {
    return reject('not_editable', proposal, {
      message:
        'A new paragraph cannot be added inside a table. Copy the text and paste it into the cell.',
    });
  }

  const located = { start, end };

  // The quote used to have to run through the anchor block's final character;
  // anything trailing was refused as `anchor_not_trailing`. That produced the
  // "we could not tell exactly where this new paragraph should go" notes, and
  // it was strictness without a purpose: the insertion is placed after the
  // whole block either way (`landingSiteAfter(anchor).after(marker)`), so a
  // quote ending mid-paragraph and one ending at its close resolve to the same
  // insertion point. Refusing only discarded the suggestion.
  //
  // It also refused the most natural thing a model does — quoting a heading or
  // a first sentence to mean "in this section" — which is exactly the case
  // where snapping forward gives the right answer.
  //
  // `snappedToBlockEnd` records that the placement is an interpretation rather
  // than a transcription, so a caller can say so rather than implying we found
  // the precise spot.
  const hasTrailingProse = (index.spans || []).some(
    span =>
      span.end > located.end &&
      anchor.contains(span.node) &&
      index.text.slice(Math.max(span.start, located.end), span.end).trim(),
  );
  const hasTrailingAtom = (index.atomSpans || []).some(
    atom => atom.start >= located.end && anchor.contains(atom.element),
  );

  return {
    ok: true,
    start: located.start,
    end: located.end,
    anchor,
    snappedToBlockEnd: hasTrailingProse || hasTrailingAtom,
  };
}

/** Resolve a delete_block quote to one existing [pid] through index spans. */
export function locateDeleteBlock(index, proposal) {
  const located = locate(index, {
    ...proposal,
    kind: 'text_patch',
    replace: proposal.find,
  });
  if (!located.ok) return located;

  const matchedSpans = (index.spans || []).filter(span =>
    overlaps(located.start, located.end, span),
  );
  const blocks = Array.from(
    new Set(
      matchedSpans.map(span => closestPidElement(span.node)).filter(Boolean),
    ),
  );
  if (blocks.length !== 1) {
    return reject('not_editable', proposal, {
      message:
        'The quoted text does not resolve to exactly one document block.',
    });
  }

  const target = blocks[0];
  // A generated list number is rebuilt after deletion; semantic atoms are
  // automation-owned content and retain the editor's fail-closed behavior.
  const protectedAtoms = (index.atomSpans || []).filter(
    atom => atom.kind !== 'n' && target.contains(atom.element),
  );
  // A block can hold both a lease-var and a crr. The cross-reference is the
  // more consequential fact — deleting it silently breaks every reference that
  // resolves through it — so it names the refusal whenever one is present,
  // rather than whichever atom happens to come first in document order.
  const crossReference = protectedAtoms.find(atom => atom.kind === 'x');
  if (crossReference) {
    return reject(UNSUPPORTED_REASONS.crossReferenceBlock, proposal, {
      atom: crossReference.ordinal,
    });
  }
  if (protectedAtoms.length > 0) {
    return reject('mutates_atom', proposal, {
      message: 'A block containing automated content cannot be deleted safely.',
      atom: protectedAtoms[0].ordinal,
    });
  }

  return {
    ok: true,
    start: located.start,
    end: located.end,
    pid: target.getAttribute('pid'),
    target,
  };
}

function normalizedPipeRow(value) {
  return foldTypography(unescapeFromProjection(String(value || '')))
    .trim()
    .replace(/^\|\s*/, '')
    .replace(/\s*\|$/, '')
    .replace(/\s+/g, ' ');
}

function directTableCells(row) {
  return Array.from(row.children).filter(cell => cell.matches('td, th'));
}

function projectionLine(index, offset) {
  const start = index.text.lastIndexOf('\n', Math.max(0, offset - 1)) + 1;
  const newline = index.text.indexOf('\n', offset);
  const end = newline === -1 ? index.text.length : newline;
  return { start, end, text: index.text.slice(start, end) };
}

/** Resolve a pipe-table quote to exactly one editable, non-generated tr. */
export function locateTableRow(index, proposal, anchorProperty) {
  const quote = normalizedPipeRow(proposal[anchorProperty]);
  if (!quote) return reject('table_row_not_found', proposal);

  const rows = new Map();
  (index.spans || []).forEach(span => {
    const row = elementForNode(span.node)?.closest('tr');
    if (!row) return;
    if (!rows.has(row)) rows.set(row, []);
    rows.get(row).push(span);
  });

  const matches = [];
  rows.forEach((spans, row) => {
    if (
      row.closest('generate-table, [ng-repeat], [data-ng-repeat]') ||
      row.hasAttribute('ng-repeat') ||
      row.hasAttribute('data-ng-repeat')
    ) {
      return;
    }

    const line = projectionLine(
      index,
      Math.min(...spans.map(span => span.start)),
    );
    if (normalizedPipeRow(line.text) !== quote) return;
    if (
      proposal.contextBefore !== undefined &&
      !foldTypography(unescapeFromProjection(index.text.slice(0, line.start)))
        .trimEnd()
        .endsWith(
          foldTypography(
            unescapeFromProjection(proposal.contextBefore),
          ).trimEnd(),
        )
    ) {
      return;
    }
    matches.push({ row, spans, line });
  });

  if (matches.length !== 1) {
    return reject('table_row_not_found', proposal, {
      ambiguous: matches.length > 1,
    });
  }

  const match = matches[0];
  const protectedAtom = (index.atomSpans || []).find(atom =>
    match.row.contains(atom.element),
  );
  if (protectedAtom) {
    return reject('mutates_atom', proposal, { atom: protectedAtom.ordinal });
  }
  if (match.spans.some(span => span.kind !== 'editable')) {
    return reject('table_row_not_found', proposal);
  }

  // Both operations require every cell to be free-text content, because the
  // only thing the save path can persist for a row is its free-text sections.
  // Beyond that the two shapes differ: an insertion maps one authored value
  // onto one cell, so its template must hold exactly one free text per cell,
  // while a deletion carries no authored cells at all — it blanks every free
  // text the row owns. Holding a deletion to the one-per-cell rule would
  // refuse the shipped rent-summary shape, whose first cell carries two
  // paragraphs (spec/js/document_patch/table_cell_text.test.js).
  const cells = directTableCells(match.row);
  const freeTextsPerCell = cells.map(
    cell => cell.querySelectorAll('[free-text]').length,
  );
  const authoredCells = anchorProperty === 'after' ? proposal.cells : null;
  if (
    cells.length === 0 ||
    freeTextsPerCell.some(count => count === 0) ||
    (anchorProperty === 'after' &&
      (!Array.isArray(authoredCells) ||
        cells.length !== authoredCells.length ||
        freeTextsPerCell.some(count => count !== 1)))
  ) {
    return reject('table_row_shape', proposal);
  }

  return {
    ok: true,
    start: match.line.start,
    end: match.line.end,
    row: match.row,
    template: match.row.cloneNode(true),
  };
}

/**
 * Route grounded second-pass proposals to their owning review surfaces.
 * Field conflicts are resolved through the immutable projection's atom table
 * and block insertions are resolved to existing DOM blocks. Only text patches
 * enter the prose redline compiler.
 */
export function routeDocumentProposals(index, proposals, attrs = {}) {
  if (!Array.isArray(proposals)) {
    throw new TypeError(
      'routeDocumentProposals requires an array of proposals',
    );
  }

  const textPatches = [];
  const fieldChanges = [];
  const blockInsertions = [];
  const blockDeletions = [];
  const rowInsertions = [];
  const rowDeletions = [];
  const rejected = [];

  proposals.forEach((proposal, proposalIndex) => {
    if (
      !String(
        proposal && proposal.sourceQuote ? proposal.sourceQuote : '',
      ).trim()
    ) {
      rejected.push({
        proposalIndex,
        result: reject('source_quote_missing', proposal || {}),
      });
      return;
    }

    if (proposal.kind === 'field_conflict') {
      const result = fieldChangeForConflict(index, proposal);
      if (result.ok) fieldChanges.push(result.change);
      else rejected.push({ proposalIndex, result });
      return;
    }

    if (proposal.kind === 'text_patch') {
      textPatches.push(proposal);
      return;
    }

    if (proposal.kind === 'insert_block') {
      const result = locateInsertBlock(index, proposal);
      if (result.ok) {
        blockInsertions.push({
          proposalIndex,
          proposal,
          anchor: result.anchor,
        });
      } else {
        rejected.push({ proposalIndex, result });
      }
      return;
    }

    if (proposal.kind === 'delete_block') {
      const result = locateDeleteBlock(index, proposal);
      if (result.ok) {
        blockDeletions.push({
          proposalIndex,
          proposal,
          pid: result.pid,
          target: result.target,
        });
      } else {
        rejected.push({ proposalIndex, result });
      }
      return;
    }

    if (proposal.kind === 'insert_row') {
      const result = locateTableRow(index, proposal, 'after');
      if (result.ok) {
        rowInsertions.push({
          proposalIndex,
          proposal,
          anchor: result.row,
          template: result.template,
        });
      } else {
        rejected.push({ proposalIndex, result });
      }
      return;
    }

    if (proposal.kind === 'delete_row') {
      const result = locateTableRow(index, proposal, 'find');
      if (result.ok) {
        rowDeletions.push({
          proposalIndex,
          proposal,
          target: result.row,
        });
      } else {
        rejected.push({ proposalIndex, result });
      }
      return;
    }

    rejected.push({
      proposalIndex,
      result: reject('not_editable', proposal, {
        message: `Proposal kind ${String(proposal.kind)} is not supported yet.`,
      }),
    });
  });

  const redline = compileTextPatches(index, textPatches, attrs);
  redline.results.forEach((result, textPatchIndex) => {
    if (result.reason !== 'field_conflict') return;

    result.conflicts.forEach(conflict => {
      const routed = fieldChangeForConflict(index, conflict);
      if (routed.ok) fieldChanges.push(routed.change);
      else rejected.push({ textPatchIndex, result: routed });
    });
  });

  return {
    redline,
    fieldChanges,
    blockInsertions,
    blockDeletions,
    rowInsertions,
    rowDeletions,
    rejected,
  };
}

function schemaRejectionNote(rejection) {
  const entry = rejection && rejection.entry;
  const reason = rejection && rejection.reason;

  return {
    kind: 'drafter_note',
    reason: 'schema_validation',
    message: `A malformed generated change was dropped: ${reason}`,
    validationReason: reason,
    modelOutputIndex: rejection && rejection.index,
    sourceQuote: entry && entry.sourceQuote,
    rationale: entry && entry.rationale,
  };
}

/**
 * Enforce the closed model-output schema before any proposal reaches routing.
 * The returned proposal array is also the only safe input for redline
 * integration, whose text-patch indexes must match the compiler's indexes.
 */
export function validateModelOutputAndRouteProposals(
  index,
  modelOutput,
  attrs = {},
  logger = console,
) {
  const validation = validateModelOutput(modelOutput, logger);

  return {
    proposals: validation.patches,
    rejected: validation.rejected.map(schemaRejectionNote),
    routed: routeDocumentProposals(index, validation.patches, attrs),
  };
}
